import { ArgumentNotSuppliedError } from "client_legacy/ndaw/errors";
import {
	GetExtendedInformationForHierarchyNodesResponseModel,
	getExtendedInformationForHierarchyNodes,
	DocumentsViewContainerViewModelExtendedInfo
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import { ExtendedInfo } from "client/Api/Documents/SharePoint/common";
import { SharePointEvent, publish } from "./documentEvents";
import { debounce } from "client/Misc/utils";
import { unique } from "client_legacy/ndaw/arrayHelpers";


type ExtendedInfoRequestBatch = {
	number: number;
	siteUrls: string[];
};

class ExtendedInfoRequester {
	private _batchNumberCounter: number;
	private readonly _pendingSiteUrlRequests: Map<string, number>;

	private _queuedSiteUrls: string[];
	private readonly _debouncedRequestBatch: () => void;

	constructor() {
		this._batchNumberCounter = 0;
		this._queuedSiteUrls = [];
		this._pendingSiteUrlRequests = new Map<string, number>();

		// Debounced so we don't consume to many concurrent requests of the browser (it can be
		// a long running operation)
		this._debouncedRequestBatch = debounce(() => this.requestBatch(), 250);
	}

	public queueFor(siteUrls: readonly string[]): void {

		if (!siteUrls) {
			throw new ArgumentNotSuppliedError("containerIds");
		}

		if (siteUrls.length === 0) {
			return;
		}

		this._queuedSiteUrls.push(...siteUrls);

		this._debouncedRequestBatch();
	}

	private async requestBatch(): Promise<void> {
		const batch: ExtendedInfoRequestBatch | null = this.prepareBatch();

		if (!batch) {
			return;
		}

		try {
			const response: GetExtendedInformationForHierarchyNodesResponseModel = await getExtendedInformationForHierarchyNodes(
				batch.siteUrls
			);

			this.broadcastExtendedInfo(response);

		} catch (e) {
			console.error("Failed to load the extended information for hierarchy nodes");
			console.error(e);
		} finally {
			this.markBatchAsFinished(batch);
		}
	}

	private prepareBatch(): ExtendedInfoRequestBatch | null {
		const batchNumber: number = ++this._batchNumberCounter;

		const siteUrls: string[] = [];

		for (const siteUrl of unique(this._queuedSiteUrls)) {
			if (this._pendingSiteUrlRequests.has(siteUrl)) {
				continue;
			}

			siteUrls.push(siteUrl);
			this._pendingSiteUrlRequests.set(siteUrl, batchNumber);
		}

		this._queuedSiteUrls = [];

		if (siteUrls.length === 0) {
			return null;
		}

		return {
			number: batchNumber,
			siteUrls
		};
	}

	private markBatchAsFinished(batch: ExtendedInfoRequestBatch) {
		for (const [siteUrl, batchNumber] of this._pendingSiteUrlRequests.entries()) {
			if (batchNumber === batch.number) {
				this._pendingSiteUrlRequests.delete(siteUrl);
			}
		}
	}

	private broadcastExtendedInfo(model: GetExtendedInformationForHierarchyNodesResponseModel) {
		for (const containerId in model.containers) {
			if (!model.containers.hasOwnProperty(containerId)) {
				continue;
			}

			const extendedInfo: ExtendedInfo<DocumentsViewContainerViewModelExtendedInfo> = model.containers[containerId];
			publish(SharePointEvent.SiteExtendedInfoLoaded, { containerId, extendedInfo });
		}
	}
}

export {
	ExtendedInfoRequester
};
